import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ShoppingCart, User, Sparkles, Menu, X, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const fetchCartCount = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API}/cart`);
          setCartCount(response.data.length);
        } catch (e) {
          console.error("Failed to fetch cart");
        }
      }
    };
    fetchCartCount();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-4 left-4 right-4 z-50 glass-nav rounded-full" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="text-xl font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="nav-logo"
          >
            VANTAGE
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/marketplace" 
              className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid="nav-marketplace"
            >
              Marketplace
            </Link>
            <Link 
              to="/generator" 
              className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors flex items-center gap-1.5"
              data-testid="nav-generator"
            >
              <Sparkles className="w-4 h-4 text-indigo-500" />
              AI Generator
            </Link>
          </div>

          {/* Actions */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link
                  to="/cart"
                  className="relative p-2 hover:bg-zinc-100 rounded-full transition-colors"
                  data-testid="nav-cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-500 text-white text-xs rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
                      data-testid="nav-user-menu"
                    >
                      <User className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer" data-testid="nav-dashboard">
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    {user?.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer" data-testid="nav-admin">
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer" data-testid="nav-profile">
                        Profile Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="cursor-pointer text-red-600"
                      data-testid="nav-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link
                to="/auth"
                className="btn-primary text-sm py-2 px-6"
                data-testid="nav-signin"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pt-4 pb-2 border-t border-zinc-200 mt-3 animate-fade-in">
            <div className="flex flex-col gap-3">
              <Link
                to="/marketplace"
                className="text-sm font-medium text-zinc-600 hover:text-zinc-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Marketplace
              </Link>
              <Link
                to="/generator"
                className="text-sm font-medium text-zinc-600 hover:text-zinc-900 py-2 flex items-center gap-1.5"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Sparkles className="w-4 h-4 text-indigo-500" />
                AI Generator
              </Link>
              {user ? (
                <>
                  <Link
                    to="/cart"
                    className="text-sm font-medium text-zinc-600 hover:text-zinc-900 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Cart {cartCount > 0 && `(${cartCount})`}
                  </Link>
                  <Link
                    to="/dashboard"
                    className="text-sm font-medium text-zinc-600 hover:text-zinc-900 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-sm font-medium text-red-600 py-2 text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  className="btn-primary text-sm py-2 text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
